// utils/syncWindow.js
// Upload settings per factory (site): the sync window that keeps photo uploads
// off the factory wifi during working hours, and the size photos are shrunk to.
// The server sends them with the site data (GET /api/sites); the tablet keeps the
// last known copy so the window also holds offline and after a restart.
//
// The window logic between the markers is mirrored in public/sw.js (a static
// file that cannot import from src); syncWindow.test.js runs the same cases
// against both copies so they cannot drift apart.

export const DEFAULT_TIMEZONE = 'Europe/Warsaw';
const SETTINGS_STORAGE_KEY = 'photoapp_site_upload_settings';

// <sync-window>
function parseClockMinutes(hhmm) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '');
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

// Minutes since midnight on the factory's wall clock, whatever zone the tablet
// itself is set to. null when the zone is unknown to this browser.
function minutesOfDayInZone(date, timeZone) {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(date);
    const read = (type) => Number(parts.find((p) => p.type === type).value);
    return (read('hour') % 24) * 60 + read('minute');
  } catch (e) {
    return null;
  }
}

// Where "now" sits relative to the window: { open, minutesUntilOpen }.
// A window that is off, incomplete or unreadable counts as open: a
// configuration mistake must never hold photos back.
function syncWindowState(settings, now) {
  const alwaysOpen = { open: true, minutesUntilOpen: 0 };
  if (!settings || !Number(settings.sync_window_enabled)) return alwaysOpen;
  const start = parseClockMinutes(settings.sync_window_start);
  const end = parseClockMinutes(settings.sync_window_end);
  if (start === null || end === null || start === end) return alwaysOpen;
  let minute = minutesOfDayInZone(now, settings.timezone || 'Europe/Warsaw');
  if (minute === null) minute = minutesOfDayInZone(now, 'Europe/Warsaw');
  if (minute === null) return alwaysOpen;
  // start > end is a window across midnight, e.g. 16:00-06:00.
  const open = start < end
    ? minute >= start && minute < end
    : minute >= start || minute < end;
  return open ? alwaysOpen : { open: false, minutesUntilOpen: (start - minute + 1440) % 1440 };
}

// Milliseconds until the window next opens (also while it is open now: the
// opening after this one). null when there is no usable window.
function msUntilWindowOpens(settings, now) {
  if (!settings || !Number(settings.sync_window_enabled)) return null;
  const start = parseClockMinutes(settings.sync_window_start);
  const end = parseClockMinutes(settings.sync_window_end);
  if (start === null || end === null || start === end) return null;
  let minute = minutesOfDayInZone(now, settings.timezone || 'Europe/Warsaw');
  if (minute === null) minute = minutesOfDayInZone(now, 'Europe/Warsaw');
  if (minute === null) return null;
  const minutes = ((start - minute + 1440) % 1440) || 1440;
  return minutes * 60000 - (now.getSeconds() * 1000 + now.getMilliseconds());
}

// Safety net: the window may delay a photo, never strand it. A photo that has
// waited this long goes out at the next opportunity, window or not.
const HELD_MAX_AGE_MS = 24 * 60 * 60 * 1000;
function heldTooLong(item, now) {
  const created = item && item.created_at ? new Date(item.created_at).getTime() : NaN;
  return Number.isFinite(created) && now.getTime() - created >= HELD_MAX_AGE_MS;
}

// May this queued item go out now? N/A markers carry no file and always pass.
function mayUploadNow(item, windowOpen, now) {
  return !!windowOpen || !!item.is_na || heldTooLong(item, now);
}
// </sync-window>

export {
  parseClockMinutes, minutesOfDayInZone, syncWindowState,
  msUntilWindowOpens, heldTooLong, mayUploadNow, HELD_MAX_AGE_MS,
};

export function isWithinSyncWindow(settings, now = new Date()) {
  return syncWindowState(settings, now).open;
}

// ── Last known settings of this tablet's own site ────────────────────────────

export function loadUploadSettings() {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

export function storeUploadSettings(settings) {
  try {
    if (settings) localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    else localStorage.removeItem(SETTINGS_STORAGE_KEY);
  } catch {
    // Storage full or blocked: the in-memory copy in SyncContext still applies.
  }
}

// Only the fields the tablet acts on, so a site rename does not rewrite the copy.
export function pickUploadSettings(site) {
  if (!site) return null;
  return {
    site_id: site.id,
    sync_window_enabled: Number(site.sync_window_enabled) ? 1 : 0,
    sync_window_start: site.sync_window_start || null,
    sync_window_end: site.sync_window_end || null,
    timezone: site.timezone || DEFAULT_TIMEZONE,
    photo_max_side: site.photo_max_side || null,
    photo_jpeg_quality: site.photo_jpeg_quality || null,
  };
}

// Photos may upload right now. Read at the moment of use, so a window that
// opened or closed since the last render is honoured.
export function isUploadWindowOpen(now = new Date()) {
  return isWithinSyncWindow(loadUploadSettings(), now);
}
